import { decamelizeKeys } from "humps";
import isNil from "lodash/isNil";
import { WebSocket as PartyWebSocket } from "partysocket";

import { buildChatWsEndpoint } from "./helpers/build_chat_ws_endpoint";
import { buildHandshakeUrl } from "./helpers/build_handshake_url";
import { makeSubscriber } from "./helpers/make_subscriber";
import type {
  ConversationId,
  ProPilotFrame,
  ProPilotMessagePayload,
  ProPilotSocket,
  ProPilotSocketFactory
} from "./types";

const WS_OPEN = 1;

const enum ReconnectableCloseCode {
  AbnormalClosure = 1006, // idle timeout / proxy drop
  GoingAway = 1001,
  TryAgainLater = 1013
}

const RECONNECTABLE_CLOSE_CODES = new Set<number>([
  ReconnectableCloseCode.AbnormalClosure,
  ReconnectableCloseCode.GoingAway,
  ReconnectableCloseCode.TryAgainLater
]);

const isReconnectable = (code?: number): boolean =>
  typeof code === "number" && RECONNECTABLE_CLOSE_CODES.has(code);

export const createAtlasProPilotSocket: ProPilotSocketFactory = ({ getToken }) => {
  const subscribers = new Set<(frame: ProPilotFrame) => void>();
  const errorSubscribers = new Set<(reason: string) => void>();
  let raw: PartyWebSocket | null = null;
  let currentConversationId: ConversationId | null = null;
  let isReconnecting = false;
  let isAutoReconnecting = false;

  const emitError = (reason: string) => {
    if (isReconnecting || isAutoReconnecting) {
      return;
    }
    [...errorSubscribers].forEach((cb) => cb(reason));
  };

  const attachPersistentListeners = (ws: PartyWebSocket) => {
    let pendingErrorMessage: string | null = null;

    ws.addEventListener("message", (event: unknown) => {
      const { data } = event as { data?: string };
      if (typeof data !== "string") {
        return;
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(data);
      } catch {
        return;
      }
      if (typeof parsed !== "object" || parsed === null || !("type" in parsed)) {
        return;
      }
      const frame = parsed as ProPilotFrame;
      [...subscribers].forEach((cb) => cb(frame));
    });

    ws.addEventListener("open", () => {
      if (ws !== raw) {
        return;
      }
      isAutoReconnecting = false;
    });

    ws.addEventListener("error", (event: unknown) => {
      const message = (event as { message?: string })?.message;
      pendingErrorMessage = typeof message === "string" && message.length > 0 ? message : "error";
    });

    ws.addEventListener("close", (event: unknown) => {
      if (ws !== raw) {
        return;
      }
      const closeEvent = event as { reason?: string; code?: number };
      if (isReconnectable(closeEvent.code)) {
        isAutoReconnecting = true;
        pendingErrorMessage = null;
        return;
      }
      isAutoReconnecting = false;
      const reason =
        pendingErrorMessage ??
        (typeof closeEvent.reason === "string" && closeEvent.reason.length > 0
          ? closeEvent.reason
          : undefined) ??
        (typeof closeEvent.code === "number" ? String(closeEvent.code) : undefined) ??
        "closed";
      pendingErrorMessage = null;
      emitError(reason);
    });
  };

  const openSocket = (conversationId: ConversationId): Promise<void> => {
    const token = getToken();
    if (isNil(token)) {
      return Promise.reject(new Error("[atlasProPilotSocket] getToken() returned null; cannot connect"));
    }
    const endpoint = buildChatWsEndpoint(conversationId);
    const url = buildHandshakeUrl(endpoint, token);

    raw = new PartyWebSocket(url);
    currentConversationId = conversationId;

    attachPersistentListeners(raw);

    return new Promise<void>((resolve, reject) => {
      const cleanup = () => {
        raw?.removeEventListener("open", onOpen as never);
        raw?.removeEventListener("error", onErrorBeforeOpen as never);
      };
      const onOpen = () => {
        cleanup();
        resolve();
      };
      const onErrorBeforeOpen = (event: unknown) => {
        cleanup();
        const message = (event as { message?: string })?.message ?? "connection error";
        reject(new Error(message));
      };
      raw!.addEventListener("open", onOpen as never);
      raw!.addEventListener("error", onErrorBeforeOpen as never);
    });
  };

  const socket: ProPilotSocket = {
    connect: (conversationId: ConversationId) => openSocket(conversationId),

    send: (payload: ProPilotMessagePayload): boolean => {
      if (!raw || raw.readyState !== WS_OPEN) {
        return false;
      }
      raw.send(JSON.stringify(decamelizeKeys(payload)));
      return true;
    },

    onResponse: makeSubscriber(subscribers),

    onError: makeSubscriber(errorSubscribers),

    reconnect: async (lastMessageId?: string) => {
      if (isNil(currentConversationId)) {
        return;
      }
      isAutoReconnecting = false;
      isReconnecting = true;
      raw?.close();
      raw = null;
      // TODO: [SP-10251] Atlas server-side resume — when supported, append
      // `?last_message_id=<id>` to the chat WS URL below. Wire is present;
      // server support is downstream.
      try {
        await openSocket(currentConversationId);
        isReconnecting = false;
      } catch (err) {
        isReconnecting = false;
        const message = err instanceof Error ? err.message : String(err ?? "reconnect failed");
        emitError(message);
        throw err;
      }
      if (lastMessageId !== undefined) {
        void lastMessageId;
      }
    },

    close: () => {
      subscribers.clear();
      errorSubscribers.clear();
      raw?.close();
      raw = null;
      currentConversationId = null;
      isAutoReconnecting = false;
      isReconnecting = false;
    }
  };

  return socket;
};
